<template>
  <div class="modal-content">
    <div class="modal-header">
      <button ref="applyBtn" @click="apply" :disabled="!dirty && !hasError">
        {{ hasError ? '⚠ error' : 'apply' }}
      </button>
      <button ref="cb64" @click="copyBase64">
        copy link
      </button>
      <button @click="load">
        open in new tab
      </button>
      <span v-if="errorMsg" class="error-msg">{{ errorMsg }}</span>
    </div>
    <vue-monaco-editor
      v-if="config"
      v-model:value="updated"
      :options="options"
      theme="vs-dark"
    />
  </div>
</template>


<script>
import { emitter } from '@/util/emitter.js';
import { compile } from '@/util/bpl/compiler.js';
import { getPresetSource } from '@/util/walls/load-wall-bpl.js';

const MONACO_EDITOR_OPTIONS = {
  automaticLayout: true,
  formatOnType: true,
  formatOnPaste: true,
  minimap: {
    enabled: false,
  },
  scrollBeyondLastLine: false,
  selectionHighlight: false,
  language: 'plaintext',
  wordWrap: 'on',
  fontSize: 14,
  lineNumbers: 'on',
  tabSize: 2,
};

export default {
  name: 'EditConfigModal',
  props: {
    config: Object,
  },
  watch: {
    config(newVal) {
      if (newVal && newVal._bplSource) {
        this.original = newVal._bplSource;
        this.updated = newVal._bplSource;
      }
    },
  },
  computed: {
    options() {
      return MONACO_EDITOR_OPTIONS;
    },
    b64() {
      return 'b64:' + btoa(this.updated);
    },
    dirty() {
      return this.original !== this.updated;
    },
  },
  data() {
    const source = (this.config && this.config._bplSource) || getPresetSource('default');
    return {
      original: source,
      updated: source,
      errorMsg: null,
      hasError: false,
    };
  },
  methods: {
    apply() {
      try {
        const compiled = compile(this.updated);
        compiled._bplSource = this.updated;
        this.errorMsg = null;
        this.hasError = false;
        this.original = this.updated;
        emitter.emit('apply-wall', compiled);
      } catch (e) {
        this.errorMsg = e.message;
        this.hasError = true;
      }
    },
    toggleCopied(el) {
      el.innerText = 'copied!';
      setTimeout(() => {
        el.innerText = 'copy link';
      }, 750);
    },
    copyBase64() {
      const url = new URL(window.location);
      url.searchParams.set('w', this.b64);
      navigator.clipboard.writeText(url.toString());
      this.toggleCopied(this.$refs.cb64);
    },
    load() {
      const url = new URL(window.location);
      url.searchParams.set('w', this.b64);
      window.open(url.toString(), '_blank');
    },
  },
  mounted() {
    this.$el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        emitter.emit('hide-modal');
      }
    });
  },
}
</script>

<style scoped>
.modal-content {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 60%;
  z-index: 101;
  background-color: transparent;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
@media (min-width: 400px) {
  .modal-content {
    width: 90%;
  }
}
@media (min-width: 600px) {
  .modal-content {
    width: 80%;
  }
}
.modal-header {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}
.modal-header button {
  min-width: 100px;
  border: none;
  background-color: #3b82f6;
  border-radius: 6px;
  padding: 8px 16px;
  color: white;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background-color 0.15s ease;
}
.modal-header button:hover {
  background-color: #2563eb;
}
.modal-header button:disabled {
  background-color: #ef4444;
  cursor: not-allowed;
}
.error-msg {
  color: #fca5a5;
  font-size: 13px;
  font-weight: 500;
}
</style>
